import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fab,
  IconButton,
  Modal,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useDrawer } from 'components/app/DrawerContext';
import React, { useEffect, useRef, useState } from 'react';
import { FaEnvelope, FaFilePdf, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrustConfig,
  sendInvestorCertificates
} from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';
import CertificateTemplate from './CertificateTemplate';
import {
  CertificateData,
  generateCertificatePdfBase64Raw,
  generateCertificatePdfStyled
} from './CertificateUtils';
import { useAppSelector } from 'app/hooks';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 3.78;

interface CertificateViewerProps {
  payload?: any;
  previewData?: any;
  certificateData?: CertificateData;
  rawCertificate?: any;
  handleTrustConfigurationInList?: () => void;
  onClose?: () => void;
  mode?: 'api' | 'preview';
}

interface ValidationError {
  show: boolean;
  severity: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  actionButton?: { text: string; onClick: () => void };
}

const CertificateViewer: React.FC<CertificateViewerProps> = ({
  certificateData: propCertificateData,
  previewData,
  rawCertificate,
  onClose,
  mode = 'preview',
  handleTrustConfigurationInList
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);

  const [open, setOpen] = useState(true);
  const certificateRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [displayData, setDisplayData] = useState<CertificateData | null>(null);
  const [validationError, setValidationError] = useState<ValidationError | null>(null);
  const [emailAlert, setEmailAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const { fundManagementAdminRight } = useDrawer();
  const [isAdmin] = useState(fundManagementAdminRight);
  const trustConfig = useSelector(selectTrustConfig);
  const investorEmail =
    rawCertificate?.investorsEmailId ||
    previewData?.investorEmail ||
    propCertificateData?.unitHolder ||
    '';

  const ensureDataUri = (base64: string | null | undefined): string | null => {
    if (!base64) return null;
    return base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
  };

  console.log('previewData', previewData, rawCertificate);

  const validateSignatures = (
    isApplicationPreview: boolean,
    isCertificate: boolean
  ): ValidationError | null => {
    const investorSig = ensureDataUri(trustConfig?.investorSignatureBase64);
    const managerSig = ensureDataUri(trustConfig?.trustFundManagerSignatureBase64);

    if (isApplicationPreview && !investorSig) {
      return {
        show: true,
        severity: 'error',
        title: 'Configuration Required',
        message: 'Your investor signature is not configured. Please complete your profile.',
        actionButton: handleTrustConfigurationInList
          ? { text: 'Go to Configuration', onClick: handleTrustConfigurationInList }
          : undefined
      };
    }

    if (isCertificate && !investorSig) {
      return {
        show: true,
        severity: 'error',
        title: 'Signature Missing',
        message: 'Your signature is required to issue the certificate.',
        actionButton: handleTrustConfigurationInList
          ? { text: 'Go to Configuration', onClick: handleTrustConfigurationInList }
          : undefined
      };
    }

    if (isCertificate && !managerSig) {
      return {
        show: true,
        severity: 'warning',
        title: 'Certificate Pending',
        message: "Awaiting fund manager's signature."
      };
    }

    return null;
  };

  useEffect(() => {
    const handleResize = () => {
      if (!certificateRef.current) return;
      const margin = isMobile ? 16 : 32;
      const padding = isMobile ? 8 : 24;
      const containerWidth = window.innerWidth - margin - padding;
      const alertHeight = alertRef.current?.offsetHeight || 0;
      const fabHeight = 100;
      const availableHeight = window.innerHeight - margin - padding - alertHeight - fabHeight;

      const certWidthPx = A4_WIDTH_MM * MM_TO_PX;
      const certHeightPx = A4_HEIGHT_MM * MM_TO_PX;

      const scaleW = containerWidth / certWidthPx;
      const scaleH = availableHeight / certHeightPx;
      const minScale = isMobile ? 0.3 : 0.75;

      setScale(Math.max(Math.min(scaleW, scaleH, 1), minScale));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    let finalData: CertificateData | null = null;
    let isApplicationPreview = false;
    let isCertificate = false;

    if (propCertificateData) {
      isCertificate = true;
      finalData = {
        ...propCertificateData,
        trustName:
          trustConfig?.trustName || propCertificateData.trustName || 'Unity Prosperity Trust',
        trustDeedExecutionDate:
          trustConfig?.trustDeedExecutionDate || propCertificateData.trustDeedExecutionDate,
        trustFundManagerSignatureBase64: ensureDataUri(
          trustConfig?.trustFundManagerSignatureBase64
        ),
        investorSignatureBase64: ensureDataUri(trustConfig?.investorSignatureBase64),
        action: 'certificate'
      };
    } else if (previewData) {
      isApplicationPreview = true;
      finalData = {
        certificateNumber: `PREVIEW-${Date.now().toString().slice(-6)}`,
        unitType: previewData.unitType === 'incomeUnits' ? 'Income' : 'Growth',
        unitHolder: previewData.investorName,
        units: previewData.units,
        price: previewData?.currentUnitPrice || previewData?.unitPrice,
        date: previewData.transactionDate
          ? new Date(previewData.transactionDate).toLocaleDateString('en-GB')
          : new Date().toLocaleDateString('en-GB'),
        beneficiary: previewData.investorName,
        trustee: 'Unity Prosperity Trust Trustee',
        action: 'application',
        dateOfRegistration: new Date().toLocaleDateString('en-GB'),
        executionDate: new Date().toLocaleDateString('en-GB'),
        trustName: trustConfig?.trustName || 'Unity Prosperity Trust',
        trustDeedExecutionDate:
          trustConfig?.trustDeedExecutionDate || new Date().toLocaleDateString('en-GB'),
        trustFundManagerSignatureBase64: ensureDataUri(
          trustConfig?.trustFundManagerSignatureBase64
        ),
        investorSignatureBase64: ensureDataUri(trustConfig?.investorSignatureBase64)
      };
    }

    const validation = validateSignatures(isApplicationPreview, isCertificate);
    setValidationError(validation);

    if (!validation?.show && finalData) {
      setDisplayData(finalData);
    } else {
      setDisplayData(null);
    }
  }, [propCertificateData, previewData, trustConfig, rawCertificate]);

  const handleDownload = () => {
    if (displayData) generateCertificatePdfStyled(displayData);
  };

  const handleSendEmail = async () => {
    if (validationError?.severity === 'error') {
      setEmailAlert({ open: true, message: validationError.message, severity: 'error' });
      return;
    }

    if (!displayData || !investorEmail) {
      setEmailAlert({
        open: true,
        message: 'Missing certificate data or email',
        severity: 'error'
      });
      return;
    }

    try {
      const pdfBase64 = await generateCertificatePdfBase64Raw(displayData);

      const payload = {
        certificatesData: [
          {
            investorEmail,
            investorName: displayData.unitHolder || 'Investor',
            trustName: displayData.trustName || 'Unity Prosperity Trust',
            certificateBase64: pdfBase64,
            fundManagerName: 'Mr. Yosh New Admin',
            unitsHeld: displayData.units.toString(),
            date: displayData.date.includes('/')
              ? displayData.date.split('/').reverse().join('-')
              : displayData.date,
            unitType: displayData.unitType === 'Growth' ? 'Growth Units' : 'Income Units',
            certificateNumber: displayData.certificateNumber,
            groupId: selectedProjectId
          }
        ]
      };

      await dispatch(sendInvestorCertificates(payload));
      setEmailAlert({
        open: true,
        message: `Certificate sent successfully to ${investorEmail}!`,
        severity: 'success'
      });
    } catch (err: any) {
      setEmailAlert({
        open: true,
        message: err.message || 'Failed to send email',
        severity: 'error'
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  if (validationError?.show) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4
          }}
        >
          <Alert severity={validationError.severity}>
            <Typography variant="h6">{validationError.title}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {validationError.message}
            </Typography>
          </Alert>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            {validationError.actionButton && (
              <Button
                variant="contained"
                onClick={() => {
                  validationError.actionButton?.onClick();
                  handleClose();
                }}
              >
                {validationError.actionButton.text}
              </Button>
            )}
            <Button variant="outlined" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </Box>
      </Modal>
    );
  }

  if (!displayData) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={open} onClose={handleClose} closeAfterTransition>
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'white',
            borderRadius: isMobile ? 0 : 3,
            width: { xs: '100vw', sm: '95vw' },
            height: { xs: '100vh', sm: '95vh' },
            maxWidth: '1400px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              bgcolor: 'white',
              boxShadow: 3
            }}
          >
            <FaTimes />
          </IconButton>

          <Alert
            ref={alertRef}
            severity={previewData ? 'info' : 'success'}
            sx={{ borderRadius: 0 }}
          >
            {previewData
              ? 'Application Preview — Final certificate issued after approval'
              : 'Official Unit Certificate'}
          </Alert>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 2,
              bgcolor: '#fafafa'
            }}
          >
            <Box
              ref={certificateRef}
              sx={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                transition: 'transform 0.3s'
              }}
            >
              <CertificateTemplate {...displayData} />
            </Box>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            sx={{ position: 'absolute', bottom: 32, right: 32, zIndex: 10 }}
          >
            <Tooltip title="Send via Email">
              <Fab color="secondary" onClick={handleSendEmail}>
                <FaEnvelope size={24} />
              </Fab>
            </Tooltip>
            <Tooltip title="Download PDF">
              <Fab color="primary" onClick={handleDownload}>
                <FaFilePdf size={26} />
              </Fab>
            </Tooltip>
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={emailAlert.open}
        autoHideDuration={6000}
        onClose={() => setEmailAlert({ ...emailAlert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={emailAlert.severity} variant="filled">
          {emailAlert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CertificateViewer;
